/**
 * CreditsContext - Provides global credits state management
 * Wraps useCredits hook in a context for component access
 */

import { createContext, useContext, ReactNode } from 'react';
import { useCredits, UseCreditsReturn } from '../hooks/useCredits';
import { useAuthContext } from './AuthContext';

/**
 * Credits context type
 */
type CreditsContextType = UseCreditsReturn;

/**
 * Credits context
 */
const CreditsContext = createContext<CreditsContextType | undefined>(undefined);

/**
 * Props for CreditsProvider
 */
interface CreditsProviderProps {
  /** Child components */
  children: ReactNode;
  /** Whether to auto-fetch credits on mount (default: true) */
  autoFetch?: boolean;
}

/**
 * CreditsProvider component
 * Provides credit state and actions to all child components
 * Requires AuthProvider to be a parent component
 * 
 * @param props - Provider props
 * @returns Provider component
 */
export function CreditsProvider({ children, autoFetch = true }: CreditsProviderProps) {
  const { isAuthenticated } = useAuthContext();
  const creditsState = useCredits({ isAuthenticated, autoFetch });

  return (
    <CreditsContext.Provider value={creditsState}>
      {children}
    </CreditsContext.Provider>
  );
}

/**
 * Hook to access credits context
 * Must be used within CreditsProvider
 * 
 * @returns Credit state and actions
 * @throws Error if used outside CreditsProvider
 */
export function useCreditsContext(): CreditsContextType {
  const context = useContext(CreditsContext);
  
  if (context === undefined) {
    throw new Error('useCreditsContext must be used within CreditsProvider');
  }
  
  return context;
}
